import React, { useEffect } from 'react';
import './BreakThePainTerms.css';

const CONTACT_EMAIL = 'adrian.ciuca.dev@gmail.com';

const BreakThePainTerms = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Break The Pain – Terms of Service';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <main className="btp-terms-page" lang="en">
      <article className="btp-terms-content">
          <h1>Break The Pain – Terms of Service</h1>
          <p className="btp-terms-meta">
            <strong>Last updated:</strong> 5 November 2026
          </p>
          <p className="btp-terms-meta">
            <strong>Effective from:</strong> 5 November 2026
          </p>

          <blockquote>
            <p>
              <strong>Please read these Terms carefully.</strong> They are a legal agreement between you and us. By installing or using the Break The Pain app, you agree to them. If you do not agree, please do not use the app.
            </p>
            <p>
              <strong>Health warning in short:</strong> Break The Pain is a fitness and mobility app. It is <strong>not</strong> medical advice and does not replace a doctor or physiotherapist. Talk to a medical professional before starting, exercise at your own risk, and <strong>stop immediately if you feel pain</strong>, dizziness or discomfort. See Section 4.
            </p>
          </blockquote>

          <hr />

          <h2>1. Who we are</h2>
          <p>
            The Break The Pain mobile application (the <strong>&quot;App&quot;</strong>) is operated by:
          </p>
          <ul>
            <li>
              <strong>Adrian Ciuca</strong>, an individual (<em>persoană fizică</em>) resident in Romania
            </li>
            <li>
              <strong>Address:</strong> Str. Sandorfalva nr. 1, ap. 3, Dumbrăvița, Timiș County, Romania
            </li>
            <li>
              <strong>Contact email:</strong>{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </li>
          </ul>
          <p>
            In these Terms, <strong>&quot;we&quot;</strong>, <strong>&quot;us&quot;</strong> and <strong>&quot;our&quot;</strong> mean the operator above. <strong>&quot;You&quot;</strong> means the person using the App.
          </p>

          <h2>2. About these Terms</h2>
          <p>
            2.1 These Terms of Service (the <strong>&quot;Terms&quot;</strong>) apply to your use of the App and of any related services, content and features we provide (together, the <strong>&quot;Service&quot;</strong>).
          </p>
          <p>
            2.2 Our <strong>Privacy Policy</strong> explains how we collect and use your personal data. It is a separate document and forms part of how we provide the Service. Please read it.
          </p>
          <p>
            2.3 When you download the App or buy a subscription through Google Play, the <strong>Google Play Terms of Service</strong> and Google&apos;s policies also apply to that relationship with Google. If there is a conflict between these Terms and Google&apos;s terms about billing, payments or refunds, Google&apos;s terms apply to those matters.
          </p>
          <p>
            2.4 Nothing in these Terms limits the rights you have as a consumer under mandatory Romanian or EU law.
          </p>

          <h2>3. Eligibility</h2>
          <p>
            3.1 You must be <strong>at least 16 years old</strong> to use the App.
          </p>
          <p>
            3.2 By using the App, you confirm that you are 16 or older and that you are legally able to enter into these Terms. If you are under 18, you should review these Terms with a parent or guardian, and a parent or guardian should approve any purchase.
          </p>
          <p>
            3.3 The App is intended for personal, non-commercial use by individuals.
          </p>

          <h2>4. Health and medical disclaimer – please read</h2>
          <p>
            4.1 <strong>Not medical advice.</strong> The App provides general fitness, exercise and mobility information. It is <strong>not</strong> medical advice, diagnosis or treatment and is <strong>not</strong> a medical device. We are not doctors or physiotherapists, and the App does not replace professional medical care. Programs aimed at specific areas (for example knees, back, neck or problems linked to desk sitting) are general exercise routines, not treatment for any injury or condition.
          </p>
          <p>
            4.2 <strong>Talk to a doctor first.</strong> Consult a doctor or other qualified health professional <strong>before</strong> starting any exercise program, and especially if you:
          </p>
          <ul>
            <li>
              have, or think you may have, any injury, pain, illness or medical condition (including heart, lung, joint, bone or blood pressure problems);
            </li>
            <li>are pregnant, have recently given birth, or are recovering from surgery;</li>
            <li>take medication that may affect your ability to exercise;</li>
            <li>have not exercised for a long time, or are over 40.</li>
          </ul>
          <p>
            4.3 <strong>Tests and generated workouts are not a diagnosis.</strong> The App may ask you to complete self-tests (for example mobility or strength checks) and may use your answers to suggest or generate workouts. These suggestions are produced automatically from general rules and the information you give. They are <strong>not</strong> a medical or physiotherapy assessment, cannot take into account everything about your health, and may not be suitable for you. You are responsible for deciding whether an exercise is right for you.
          </p>
          <p>
            4.4 <strong>Stop if it hurts.</strong> Stop exercising immediately and seek medical help if you feel pain (other than normal mild muscle effort), sharp or unusual discomfort, chest pain, shortness of breath, dizziness, faintness, numbness or anything else that worries you. Do not &quot;push through&quot; pain.
          </p>
          <p>
            4.5 <strong>Exercise at your own risk.</strong> Physical exercise carries a risk of injury. You choose to do the exercises voluntarily and at your own risk. Make sure:
          </p>
          <ul>
            <li>you warm up properly and use correct technique;</li>
            <li>your space is safe, clear of obstacles and has a suitable surface;</li>
            <li>
              any equipment you use (for example weights, bands, benches) is in good condition and suitable for you;
            </li>
            <li>you do not use weights that are too heavy for you.</li>
          </ul>
          <p>
            4.6 <strong>Emergencies.</strong> The App cannot help in an emergency. In Romania, call <strong>112</strong>.
          </p>
          <p>
            4.7 This Section does not exclude our liability where the law does not allow it (see Section 13).
          </p>

          <h2>5. Your account</h2>
          <p>
            5.1 Some features may require you to create an account. You agree to give accurate information and keep it up to date.
          </p>
          <p>
            5.2 You are responsible for keeping your login details safe and for activity on your account. Tell us promptly at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you think someone has accessed your account without permission.
          </p>
          <p>
            5.3 You may have only one account, and you may not share it, sell it or transfer it to anyone else.
          </p>
          <p>
            5.4 You can delete your account at any time by emailing{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Deleting your account does <strong>not</strong> cancel a Google Play subscription – see Section 7.
          </p>

          <h2>6. Free and paid features</h2>
          <p>
            6.1 <strong>Free tier.</strong> The free version of the App includes features such as bodyweight and mobility workouts. We may change which features are free over time.
          </p>
          <p>
            6.2 <strong>Premium subscription.</strong> A paid subscription (<strong>&quot;Premium&quot;</strong>) unlocks additional features, which may include workouts with weights, targeted programs (for example for knees or desk sitting), and extended workout-generator options. The exact features, price and billing period are shown in the App and on Google Play before you buy.
          </p>
          <p>
            6.3 <strong>Changes to features.</strong> We continuously develop the App. We may add, change or remove features, as long as any change to Premium does not significantly reduce what you have already paid for during your current billing period, unless the change is required by law, for security, or for reasons outside our control. If a change negatively affects your access to Premium content in a more than minor way, we will tell you in advance and you may cancel, and where required by law you may terminate and get a pro-rata refund for the remaining period.
          </p>

          <h2>7. Subscriptions, billing and auto-renewal (Google Play)</h2>
          <p>
            7.1 <strong>Purchases through Google Play.</strong> All Premium purchases are made through <strong>Google Play</strong> and processed by Google using the payment method linked to your Google account. We do not receive or store your payment card details.
          </p>
          <p>
            7.2 <strong>Price and taxes.</strong> The price, currency and billing period are shown before you confirm your purchase. Prices shown to consumers include VAT where applicable.
          </p>
          <p>
            7.3 <strong>Free trials.</strong> If we offer a free trial, the trial length and the price that will apply afterwards will be shown before you start it. <strong>Unless you cancel at least 24 hours before the trial ends</strong> (or within the time Google Play states), the subscription will start and you will be charged automatically.
          </p>
          <p>
            7.4 <strong>Auto-renewal.</strong> Subscriptions <strong>renew automatically</strong> at the end of each billing period (for example monthly or yearly) and you will be charged for the next period, unless you cancel before the renewal date.
          </p>
          <p>
            7.5 <strong>How to cancel.</strong> You can cancel at any time in Google Play:
            <br />
            <strong>
              Google Play Store → your profile icon → Payments &amp; subscriptions → Subscriptions → Break The Pain → Cancel subscription.
            </strong>
            <br />
            Uninstalling the App or deleting your account does <strong>not</strong> cancel your subscription.
          </p>
          <p>
            7.6 <strong>What happens when you cancel.</strong> After cancelling, you keep Premium access until the end of the period you have already paid for. You will not be charged again. Except as set out in Section 8 or required by law, we do not give refunds for partial billing periods.
          </p>
          <p>
            7.7 <strong>Price changes.</strong> We may change subscription prices for future periods. Google Play will notify you in advance as required by its policies and applicable law, and where required you will be asked to accept the new price. If you do not agree, you can cancel before the new price applies.
          </p>
          <p>
            7.8 <strong>Refunds.</strong> Refund requests for Google Play purchases are generally handled by Google under the <strong>Google Play refund policy</strong> (see support.google.com/googleplay). You can also contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will help where we can. This does not affect your statutory rights.
          </p>
          <p>
            7.9 <strong>Restoring purchases.</strong> If you change device, you can restore your subscription by signing in with the same Google account.
          </p>

          <h2>8. Your 14-day right of withdrawal (EU consumers)</h2>
          <p>
            8.1 <strong>General rule.</strong> If you are a consumer in the EU, you normally have the right to withdraw from a distance contract within <strong>14 days</strong> without giving a reason (Romanian Government Emergency Ordinance no. 34/2014, implementing EU Directive 2011/83/EU).
          </p>
          <p>
            8.2 <strong>Digital content and services – immediate access.</strong> Premium gives you immediate access to digital content and features. <strong>When you buy Premium, you request that access starts immediately, during the 14-day withdrawal period, and you acknowledge that you lose your right of withdrawal once access to the paid digital content has started</strong> (as permitted by Article 16 letter m) of GEO 34/2014 and Article 16(m) of Directive 2011/83/EU). This consent and acknowledgement is requested at the point of purchase, and we will confirm it to you (for example by email or in the App) after purchase.
          </p>
          <p>
            8.3 <strong>If you have not consented as described above</strong>, or if the consent was not properly obtained, you may withdraw within 14 days from the day the contract was concluded. To do so, send us a clear statement (for example by email to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>) or use the model withdrawal form below, and request a refund through Google Play or from us. Where the service was already provided at your request during the withdrawal period, you may have to pay an amount proportionate to what was provided until you told us you were withdrawing, where the law allows this.
          </p>
          <p>
            8.4 <strong>Free trials.</strong> If you start a free trial, you can cancel at any time during the trial at no cost (see Section 7.3).
          </p>
          <p>
            8.5 <strong>Model withdrawal form</strong> (fill in and send only if you want to withdraw):
          </p>
          <blockquote>
            To: Adrian Ciuca, Str. Sandorfalva nr. 1, ap. 3, Dumbrăvița, Timiș County, Romania,{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            <br />
            I hereby give notice that I withdraw from my contract for the following digital service: Break The Pain Premium subscription.
            <br />
            Ordered on: [date] / Google Play order number: [GPA.xxxx]
            <br />
            Name: [your name]
            <br />
            Email / Google account used: [your email]
            <br />
            Date: [date]
          </blockquote>

          <h2>9. Conformity of the App (your legal guarantee)</h2>
          <p>
            9.1 As a consumer, you have legal rights if the App or paid digital content is not as described or does not work as it should, under Romanian Government Emergency Ordinance no. 141/2021 (implementing EU Directive 2019/770 on digital content and digital services). These rights may include having the problem fixed, a price reduction or ending the contract and getting a refund.
          </p>
          <p>
            9.2 If something is not working, contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and describe the issue. We will try to resolve it within a reasonable time.
          </p>

          <h2>10. Beta version</h2>
          <p>
            10.1 The App is currently offered as a <strong>beta</strong> (test) version and some features may also be released as beta in the future. Beta features are still in development and may:
          </p>
          <ul>
            <li>contain bugs, errors or inaccurate content;</li>
            <li>be changed, interrupted or removed without notice;</li>
            <li>lose data (for example workout history or progress).</li>
          </ul>
          <p>
            10.2 Beta features are provided free of charge and <strong>&quot;as is&quot;</strong>, to the extent permitted by law. Please do not rely on them for anything important, and please report problems to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Feedback you send us helps improve the App (see Section 12.4).
          </p>
          <p>
            10.3 If you pay for Premium while parts of the App are in beta, your consumer rights under Sections 8 and 9 still apply to what you paid for.
          </p>

          <h2>11. Acceptable use</h2>
          <p>
            You agree <strong>not</strong> to:
          </p>
          <ul>
            <li>use the App for any unlawful purpose or in breach of these Terms;</li>
            <li>
              copy, sell, rent, share, publish or redistribute the App&apos;s content (including videos, images, programs and exercise descriptions), except as allowed by law;
            </li>
            <li>
              share your Premium access or account with others, or bypass any payment, access or security measures;
            </li>
            <li>
              reverse-engineer, decompile or disassemble the App, except where the law allows this despite this restriction;
            </li>
            <li>use bots, scrapers or other automated tools to access or copy the App or its content;</li>
            <li>
              interfere with or damage the App, its servers or networks, or introduce viruses or malicious code;
            </li>
            <li>
              upload or send content that is illegal, offensive, harassing, misleading, or that infringes other people&apos;s rights;
            </li>
            <li>impersonate anyone or give false information.</li>
          </ul>

          <h2>12. Intellectual property and your content</h2>
          <p>
            12.1 <strong>Our content.</strong> The App and everything in it – including software, design, logos, the &quot;Break The Pain&quot; name, exercise programs, videos, images, text and the workout-generator logic – belong to us or our licensors and are protected by intellectual property laws.
          </p>
          <p>
            12.2 <strong>Your licence.</strong> We give you a personal, non-exclusive, non-transferable, revocable licence to install and use the App on devices you own or control, for your own private, non-commercial use, in line with these Terms and Google Play&apos;s rules. We do not grant you any other rights.
          </p>
          <p>
            12.3 <strong>Your data and content.</strong> You may enter information into the App, such as test results, workout logs, progress notes or profile details (<strong>&quot;Your Content&quot;</strong>). You keep ownership of Your Content. You give us permission to store and process it only as needed to provide and improve the Service, as described in our Privacy Policy.
          </p>
          <p>
            12.4 <strong>Feedback.</strong> If you send us ideas, suggestions or feedback, we may use them freely to improve the App without any obligation to you. We will not publicly identify you without your permission.
          </p>

          <h2>13. Our liability</h2>
          <p>
            13.1 <strong>What we are responsible for.</strong> We provide the Service with reasonable care and skill. If we fail to comply with these Terms, we are responsible for loss or damage you suffer that is a foreseeable result of our breach or our negligence.
          </p>
          <p>
            13.2 <strong>What we never exclude.</strong> Nothing in these Terms excludes or limits our liability for:
          </p>
          <ul>
            <li>death or personal injury caused by our fault (including negligence);</li>
            <li>damage caused intentionally or through gross negligence;</li>
            <li>fraud or fraudulent misrepresentation;</li>
            <li>your statutory consumer rights (including Sections 8 and 9);</li>
            <li>any other liability that cannot be excluded or limited under Romanian or EU law.</li>
          </ul>
          <p>13.3 <strong>Limits, where the law allows.</strong> Subject to Section 13.2:</p>
          <ul>
            <li>
              we are not responsible for injury or loss that results from you ignoring the health warnings in Section 4, from not following exercise instructions, from exercising with a medical condition without medical advice, or from unsafe equipment or surroundings;
            </li>
            <li>
              we are not responsible for losses that were not foreseeable, or for business losses (the App is for personal use only);
            </li>
            <li>
              we are not responsible for problems caused by your device, internet connection, Google Play, or other events outside our reasonable control;
            </li>
            <li>
              for the free tier and beta features, which you receive at no cost, our liability is limited to the extent permitted by law;
            </li>
            <li>
              for Premium, our total liability to you for any claim related to the Service is limited to the amount you paid for Premium in the 12 months before the event giving rise to the claim.
            </li>
          </ul>

          <h2>14. Suspension and termination</h2>
          <p>
            14.1 <strong>By you.</strong> You can stop using the App at any time. To end a subscription, cancel it in Google Play (Section 7.5). You can also delete your account (Section 5.4).
          </p>
          <p>
            14.2 <strong>By us.</strong> We may suspend or end your access to the App if you seriously or repeatedly break these Terms, use the App unlawfully, or if we are required to do so by law. Where reasonable, we will warn you first and explain why. If we end your access without you being at fault, we will refund any prepaid amount for the unused part of your Premium subscription.
          </p>
          <p>
            14.3 <strong>Discontinuing the App.</strong> We may decide to stop offering the App. If so, we will give you reasonable advance notice and refund any prepaid amount for the period after the App stops working.
          </p>
          <p>
            14.4 Sections that by their nature should continue after termination (for example Sections 4, 12, 13 and 16) will continue to apply.
          </p>

          <h2>15. Changes to these Terms</h2>
          <p>
            15.1 We may update these Terms, for example to reflect changes in the law, in the App, or in how we operate (including when the App leaves beta and paid features are introduced).
          </p>
          <p>
            15.2 We will notify you of important changes in the App or by email <strong>at least 30 days</strong> before they take effect, unless a change is needed sooner for legal or security reasons. The &quot;Last updated&quot; date at the top will always show the current version.
          </p>
          <p>
            15.3 If you do not agree to the changes, you can stop using the App and cancel your subscription before the changes take effect. If you continue to use the App after that, the updated Terms will apply.
          </p>

          <h2>16. Governing law, complaints and disputes</h2>
          <p>
            16.1 <strong>Governing law.</strong> These Terms are governed by <strong>Romanian law</strong>. If you are a consumer living in another EU country, you also keep the protection of the mandatory consumer laws of your country of residence.
          </p>
          <p>
            16.2 <strong>Contact us first.</strong> If you have a complaint, please email us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We aim to reply within 14 days and to resolve complaints fairly and quickly.
          </p>
          <p>
            16.3 <strong>Romanian consumer authority (ANPC).</strong> You may also submit a complaint to the <strong>Romanian National Authority for Consumer Protection (Autoritatea Națională pentru Protecția Consumatorilor – ANPC)</strong>:{' '}
            <a href="https://anpc.ro" target="_blank" rel="noopener noreferrer">
              anpc.ro
            </a>
            , Bd. Aviatorilor nr. 72, Sector 1, Bucharest, Romania; consumer helpline (TelVerde) 0800 080 999.
          </p>
          <p>
            16.4 <strong>Alternative dispute resolution (ADR/SAL).</strong> Consumers can use out-of-court dispute resolution. In Romania, information on alternative dispute resolution (<em>Soluționarea Alternativă a Litigiilor – SAL</em>) is available from ANPC at{' '}
            <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="noopener noreferrer">
              anpc.ro/ce-este-sal
            </a>
            . We are willing to take part in ADR procedures.
          </p>
          <p>
            16.5 <strong>EU online dispute resolution.</strong> The EU Online Dispute Resolution (ODR) platform was discontinued on 20 July 2025 (Regulation (EU) 2024/3228). Consumers in other EU countries can find their national ADR bodies and help with cross-border complaints through the <strong>European Consumer Centres Network (ECC-Net)</strong>:{' '}
            <a
              href="https://commission.europa.eu/live-work-travel-eu/consumer-rights-and-complaints_en"
              target="_blank"
              rel="noopener noreferrer"
            >
              ec.europa.eu/consumers
            </a>
            .
          </p>
          <p>
            16.6 <strong>Courts.</strong> If a dispute cannot be resolved amicably, it will be settled by the competent courts of Romania. As a consumer, you may also bring proceedings in the courts of the EU country where you live, and we may only sue you in those courts.
          </p>

          <h2>17. General</h2>
          <p>
            17.1 <strong>Entire agreement.</strong> These Terms, together with our Privacy Policy and any terms shown at purchase, are the whole agreement between you and us about the Service.
          </p>
          <p>
            17.2 <strong>Severability.</strong> If any part of these Terms is found invalid or unenforceable, the rest will remain in force.
          </p>
          <p>
            17.3 <strong>No waiver.</strong> If we do not enforce a right straight away, we can still enforce it later.
          </p>
          <p>
            17.4 <strong>Transfer.</strong> We may transfer our rights and obligations under these Terms to another business (for example if the App is transferred to a company we set up). We will tell you, and this will not reduce your rights. You may not transfer your rights under these Terms without our consent.
          </p>
          <p>
            17.5 <strong>Third parties.</strong> Google is not a party to these Terms and is not responsible for the App or its content, except as Google&apos;s own terms state. No other third party has rights under these Terms.
          </p>
          <p>
            17.6 <strong>Language.</strong> These Terms are provided in English. If we provide a Romanian translation, the Romanian version will prevail in case of conflict, unless the law requires otherwise.
          </p>

          <h2>18. Contact us</h2>
          <p>
            <strong>Adrian Ciuca</strong>
            <br />
            Str. Sandorfalva nr. 1, ap. 3, Dumbrăvița, Timiș County, Romania
            <br />
            Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
      </article>
    </main>
  );
};

export default BreakThePainTerms;
